/**
 * 环境配置 — 链 / 合约 / API 统一从 env 读取。
 * 本地：.env（正式）/ .env.test（测试）；Docker 构建上下文仍用 .env.production（由部署脚本写入）。
 * 字段说明见仓库根目录 `.env.example`。
 * 缺省时：非 production → BSC 测试网 97；production → BSC 主网 56。
 */

const getEnv = (): 'development' | 'test' | 'production' => {
  const mode = import.meta.env.MODE
  if (mode === 'development' || mode === 'dev') return 'development'
  if (mode === 'test' || mode === 'testing') return 'test'
  return 'production'
}

export const ENV = getEnv()
export const isDev = ENV === 'development'
export const isTest = ENV === 'test'
export const isProd = ENV === 'production'

const isDevServer = import.meta.env.DEV

const pick = (key: keyof ImportMetaEnv | string, fallback = ''): string =>
  String((import.meta.env as Record<string, string | undefined>)[key] ?? fallback).trim()

const pickAddr = (key: string, fallback: string): string => {
  const v = pick(key, fallback)
  return /^0x[a-fA-F0-9]{40}$/.test(v) ? v : fallback
}

/** —— 内置兜底（仅 env 未配时；日常以 .env* 为准）—— */
const FALLBACK = {
  api: 'https://api.pantamarket.link',
  testnet: {
    chainId: 97,
    chainName: 'BNB Smart Chain Testnet',
    rpc: 'https://bsc-testnet-rpc.publicnode.com',
    rpcFallbacks:
      'https://bsc-testnet.public.blastapi.io,https://data-seed-prebsc-1-s1.binance.org:8545/',
    explorer: 'https://testnet.bscscan.com',
    // 测试服：充值收款与提现金库共用
    usdt: '0x1eb749aEb9278c2e8Ef58193af830aE4432F12A2',
    deposit: '0xa153352d6b27ca2af0b0b3d67a1d0cd1093e983d',
    vault: '0xa153352d6b27ca2af0b0b3d67a1d0cd1093e983d',
    planet: '0xb08aA68d2C336e1ae58Dde3E0468F41f83D13e05',
  },
  mainnet: {
    chainId: 56,
    chainName: 'BNB Smart Chain',
    rpc: 'https://bsc-dataseed.binance.org',
    rpcFallbacks:
      'https://bsc-dataseed.binance.org/,https://bsc.publicnode.com',
    explorer: 'https://bscscan.com',
    // 正式服：充值收款与提现金库各用各的
    usdt: '0x55d398326f99059fF775485246999027B3197955',
    deposit: '0xc42f00AC5F6816D82331e07Ec8085c97036738Fd',
    vault: '0x408bee79d125718a3fa100cddf54ba592c46e9ef',
    planet: '0x068d576AE65600FB4CDc82e3326eF19c421121e8',
  },
} as const

const profile = isProd ? FALLBACK.mainnet : FALLBACK.testnet

// —— API / 行情 ——
export const API_BASE_URL = isDevServer
  ? '/api'
  : pick('VITE_API_BASE_URL', FALLBACK.api)

const DEFAULT_WS_URL = `${FALLBACK.api.replace(/^http/, 'ws')}/ws`
export const WS_BASE_URL = isDevServer
  ? `${typeof window !== 'undefined' && window.location?.protocol === 'https:' ? 'wss' : 'ws'}://${typeof window !== 'undefined' ? window.location.host : 'localhost:3030'}/ws`
  : pick('VITE_WS_BASE_URL', DEFAULT_WS_URL)

function resolveProdBinanceSpotWs(): string {
  const raw = pick('VITE_BINANCE_WS_URL')
  if (raw && !/\/binance-stream/i.test(raw)) return raw.replace(/\/$/, '')
  return 'wss://data-stream.binance.vision'
}

export const BINANCE_WS_URL = isDevServer
  ? 'ws://localhost:3030/binance-stream'
  : resolveProdBinanceSpotWs()

export const BINANCE_FUTURES_WS_URL = isDevServer
  ? 'ws://localhost:3030/binance-fstream'
  : pick('VITE_BINANCE_FUTURES_WS_URL', 'wss://fstream.binance.com')

export const API_TIMEOUT = Number(pick('VITE_API_TIMEOUT', '10000')) || 10000

// —— 链 ——
const parsedChainId = Number(pick('VITE_CHAIN_ID', String(profile.chainId)))
export const TARGET_CHAIN_ID =
  Number.isFinite(parsedChainId) && parsedChainId > 0
    ? parsedChainId
    : profile.chainId

export const TARGET_CHAIN_HEX = `0x${TARGET_CHAIN_ID.toString(16)}`

export const TARGET_CHAIN_NAME = pick(
  'VITE_CHAIN_NAME',
  profile.chainName
)

export const CHAIN_EXPLORER_URL = pick(
  'VITE_CHAIN_EXPLORER',
  profile.explorer
).replace(/\/$/, '')

export const BSC_RPC_URL = pick('VITE_BSC_RPC_URL', profile.rpc)

/** 额外公共 RPC，逗号分隔；读余额失败时依次尝试 */
export const BSC_RPC_FALLBACKS: string[] = pick(
  'VITE_BSC_RPC_FALLBACKS',
  profile.rpcFallbacks
)
  .split(',')
  .map((s) => s.trim())
  .filter(Boolean)

export const BSC_RPC_CANDIDATES: string[] = [
  BSC_RPC_URL,
  ...BSC_RPC_FALLBACKS,
].filter((url, i, arr) => url && arr.indexOf(url) === i)

// —— 合约 ——
/** 充值/余额用的 USDT（测试网为自建测试币） */
export const USDT_CONTRACT_ADDRESS = pickAddr(
  'VITE_USDT_CONTRACT',
  profile.usdt
)

export const USDT_DECIMALS = Number(pick('VITE_USDT_DECIMALS', '18')) || 18

export const TAKER_TOKEN_ADDRESS = pickAddr(
  'VITE_TAKER_TOKEN',
  '0xc19539eb93444523ec8f1432624924d2e6226546'
)

/**
 * 用户充值收款地址兜底。
 * 接口 `receiver_address` 仅当命中前端白名单时才采用，防止被篡改指向未知地址。
 * 测试服与金库同址；正式服与金库分离。
 */
export const DEPOSIT_WITHDRAW_CONTRACT_ADDRESS = pickAddr(
  'VITE_DEPOSIT_WITHDRAW_CONTRACT',
  profile.deposit
)

const isEthAddress = (v: string): boolean => /^0x[a-fA-F0-9]{40}$/.test(v)

/** 充值收款白名单：env 合约地址 + 可选额外地址（逗号分隔） */
export const DEPOSIT_RECEIVER_ALLOWLIST: string[] = (() => {
  const extra = pick('VITE_DEPOSIT_RECEIVER_ALLOWLIST', '')
    .split(',')
    .map((s) => s.trim())
    .filter(isEthAddress)
  const seen = new Set<string>()
  const out: string[] = []
  for (const addr of [DEPOSIT_WITHDRAW_CONTRACT_ADDRESS, ...extra]) {
    const key = addr.toLowerCase()
    if (!key || seen.has(key)) continue
    seen.add(key)
    out.push(addr)
  }
  return out
})()

/**
 * 解析可用的充值收款地址：API 地址须在白名单内，否则回退已知兜底地址。
 * @returns 合法地址；皆不可用时返回空串
 */
export function resolveDepositReceiverAddress(
  apiAddress?: string | null
): string {
  const allow = new Set(
    DEPOSIT_RECEIVER_ALLOWLIST.map((a) => a.toLowerCase())
  )
  for (const candidate of [apiAddress, DEPOSIT_WITHDRAW_CONTRACT_ADDRESS]) {
    const s = String(candidate || '').trim()
    if (isEthAddress(s) && allow.has(s.toLowerCase())) return s
  }
  return ''
}

/** 提现金库 VAULT_ADDRESS；接口 vaultAddress 优先。正式服与充值收款分离 */
export const VAULT_ADDRESS = pickAddr('VITE_VAULT_ADDRESS', profile.vault)

export const WITHDRAW_CONTRACT_ADDRESS = VAULT_ADDRESS
export const COLLECTION_ADDRESS = DEPOSIT_WITHDRAW_CONTRACT_ADDRESS

export const PLANET_PROJECT_CONTRACT_ADDRESS = pickAddr(
  'VITE_PLANET_PROJECT',
  profile.planet
)

/**
 * WalletConnect Cloud projectId（公开配置，可进仓库模板）。
 * 须在 https://cloud.walletconnect.com 为正式/测试域名完成 Allowlist + Domain Verification，
 * 否则手机钱包会显示 Unverified DApp。
 */
export const WALLETCONNECT_PROJECT_ID = pick(
  'VITE_WALLETCONNECT_PROJECT_ID',
  'ba9c96ec2aae4c6fbf9e9ec19dd0265d'
)

export const isMock =
  String(import.meta.env.VITE_MOCK ?? 'false').toLowerCase() === 'true'

/** 调试用：当前链与合约快照 */
export const chainContractConfig = {
  ENV,
  TARGET_CHAIN_ID,
  TARGET_CHAIN_HEX,
  TARGET_CHAIN_NAME,
  CHAIN_EXPLORER_URL,
  BSC_RPC_URL,
  BSC_RPC_CANDIDATES,
  USDT_CONTRACT_ADDRESS,
  USDT_DECIMALS,
  TAKER_TOKEN_ADDRESS,
  DEPOSIT_WITHDRAW_CONTRACT_ADDRESS,
  VAULT_ADDRESS,
  PLANET_PROJECT_CONTRACT_ADDRESS,
} as const

export default {
  ENV,
  isDev,
  isTest,
  isProd,
  isMock,
  API_BASE_URL,
  WS_BASE_URL,
  BINANCE_WS_URL,
  BINANCE_FUTURES_WS_URL,
  API_TIMEOUT,
  ...chainContractConfig,
}
